import { Children, cloneElement, ReactElement, useState } from 'react';

import { RadioGroup as HeadlessRadioGroup } from '@headlessui/react';
import styled from 'styled-components';

import { Badge } from '@components/badge';
import { FormError } from '@components/form-error';
import { FormLabel } from '@components/form-label';
import { AnimatedRadio } from '@components/radio-group/animated-radio';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { VisuallyHidden } from '@components/visually-hidden';
import { useEvent } from '@hooks/use-event';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';

type RadioValue = string | number;

type RadioCardGroupPropsBase = {
  label: string;
  hideLabel?: boolean;
  isDisabled?: boolean;
  defaultValue?: RadioValue;
  defaultBadgeValue?: RadioValue;
  onChange?(newValue: RadioValue): void;
  value?: RadioValue;
  error?: string;
  defaultBadgeLabel?: string;
};

export type RadioCardGroupPropsChildren = RadioCardGroupPropsBase & {
  children: Array<ReactElement<RadioCardGroupOptionProps>>;
  options?: never;
};

export type RadioCardGroupPropsOptions = RadioCardGroupPropsBase & {
  children?: never;
  options: Array<RadioCardGroupOptionProps>;
};

export type RadioCardGroupProps = RadioCardGroupPropsChildren | RadioCardGroupPropsOptions;

export const RadioCardGroup = ({
  children,
  defaultValue,
  defaultBadgeValue,
  hideLabel,
  label,
  onChange,
  options,
  error,
  value,
  isDisabled = false,
  defaultBadgeLabel = 'Default',
}: RadioCardGroupProps) => {
  const isControlled = value !== undefined;
  const [localSelected, setLocalSelected] = useState<RadioValue>(defaultValue ?? '');

  const handleChange = useEvent((newValue: RadioValue) => {
    setLocalSelected(newValue);
    onChange?.(newValue);
  });

  const selectedValue = isControlled ? value : localSelected;

  const labelElement = <HeadlessRadioGroup.Label as={FormLabel} text={label} />;

  const renderedChildren =
    children != null
      ? Children.map(children, (child) => {
          return cloneElement(child, {
            defaultBadgeValue,
            defaultBadgeLabel,
          } as Partial<RadioCardGroupOptionProps>);
        })
      : options?.map((option) => (
          <RadioCardGroupOption
            key={option.value}
            {...option}
            defaultBadgeValue={defaultBadgeValue}
            defaultBadgeLabel={defaultBadgeLabel}
          />
        ));

  return (
    <HeadlessRadioGroup
      value={selectedValue}
      onChange={handleChange}
      disabled={isDisabled}
      defaultValue={defaultValue}
    >
      <Stack space={1}>
        {hideLabel ? <VisuallyHidden>{labelElement}</VisuallyHidden> : labelElement}
        <Stack space={2}>{renderedChildren}</Stack>
        <FormError text={error} />
      </Stack>
    </HeadlessRadioGroup>
  );
};

export type RadioCardGroupOptionProps = {
  value: RadioValue;
  label: string;
  helpText?: string;
  isDisabled?: boolean;
};

type RadioCardGroupOptionInternalProps = RadioCardGroupOptionProps & {
  defaultBadgeValue?: RadioValue;
  defaultBadgeLabel?: string;
};

const RadioCardGroupOption = ({
  value,
  label,
  helpText,
  isDisabled = false,
  defaultBadgeValue,
  defaultBadgeLabel = 'Default',
}: RadioCardGroupOptionInternalProps) => (
  <HeadlessRadioGroup.Option as={OptionContainer} value={value} disabled={isDisabled}>
    {({ checked }) => (
      <Stack orientation="horizontal" align="center" justify="space-between" space={3}>
        <Stack orientation="horizontal" align="center" space={3}>
          <RadioIcon $checked={checked}>
            <AnimatedRadio checked={checked} />
          </RadioIcon>
          <Stack space={0}>
            <Text text={label} font="semibold14" />
            {helpText && <Text text={helpText} font="regular14" color="textSecondary" />}
          </Stack>
        </Stack>
        {value === defaultBadgeValue && <Badge text={defaultBadgeLabel} variant="indigo" />}
      </Stack>
    )}
  </HeadlessRadioGroup.Option>
);

RadioCardGroup.Option = RadioCardGroupOption;

const OptionContainer = styled.div({
  padding: `${pxToRem(12)} ${pxToRem(16)}`,
  border: `${pxToRem(1)} solid ${theme.color.border10}`,
  borderRadius: pxToRem(8),
  backgroundColor: 'transparent',
  outline: 'none',
  transition: transitionStandard('background-color'),

  '&[data-headlessui-state~=checked]': {
    backgroundColor: theme.color.blueTrans5,
  },

  '&[data-headlessui-state~=disabled]': {
    opacity: 0.5,
  },

  '&:not([data-headlessui-state~=disabled])': {
    cursor: 'pointer',

    '&:hover': {
      backgroundColor: theme.color.uiTrans5,
    },

    '&[data-headlessui-state~=checked]:hover': {
      backgroundColor: theme.color.blueTrans10,
    },
  },

  '&:focus-visible': {
    boxShadow: theme.shadow.focus,
  },
});

const RadioIcon = styled.span<{ $checked: boolean }>((p) => ({
  display: 'inline-flex',
  flexShrink: 0,
  color: p.$checked
    ? theme.color.interactiveElementSelected
    : theme.color.interactiveElementNeutral,
}));
